// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMyTimeZone, useZonedEvent } from "@/providers/EventTimeZoneProvider";
import { formatMine, formatZoned, primaryTimeZone } from "@/lib/timezones/zonedFormat";
/**
 * Times in `zone` (the viewer's when absent), labelled through the
 * `timezones.*` keys, so translation overrides change them. `format` and
 * `mine` take another zone for rows with their own (log rows).
 */
export var useZonedFormat = function (zone, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.seconds, seconds = _c === void 0 ? false : _c;
    var _d = useTranslation(), t = _d.t, i18n = _d.i18n;
    var lang = i18n.language;
    var myZone = useMyTimeZone();
    return useMemo(function () {
        var shown = zone || myZone;
        var options = { t: t, lang: lang, seconds: seconds };
        return {
            zone: shown,
            myZone: myZone,
            format: function (value, inZone) { return formatZoned(value, inZone || shown, options); },
            mine: function (value, inZone) { return formatMine(value, inZone || shown, options, myZone); },
        };
        // `t` changes when the language or the loaded resources do.
    }, [zone, myZone, t, lang, seconds]);
};
/**
 * The presentation of `event`: the record when the caller has it, else the
 * event of the screen (EventTimeZoneProvider) when it is that event or no
 * event is named. Nothing is read from the server.
 */
export var useEventPresentation = function (event) {
    var _a, _b;
    var screenEvent = useZonedEvent();
    if (event && typeof event === "object" && "presentation" in event) {
        return ((_a = event.presentation) !== null && _a !== void 0 ? _a : undefined);
    }
    var id = event && typeof event === "object" ? event.id : event;
    if (screenEvent && (!id || String(id) === screenEvent.id)) {
        return (_b = screenEvent.presentation) !== null && _b !== void 0 ? _b : undefined;
    }
    return undefined;
};
/**
 * Admin times of an election event: in its primary zone (on an election's
 * screens, the election's zone). Outside the event's screens, the viewer's.
 */
export var useEventZonedFormat = function (event, options) {
    var screenEvent = useZonedEvent();
    var presentation = useEventPresentation(event);
    var id = event && typeof event === "object" ? event.id : event;
    var isScreenEvent = !!screenEvent &&
        !(event && typeof event === "object" && "presentation" in event) &&
        (!id || String(id) === screenEvent.id);
    var zone = isScreenEvent
        ? screenEvent.zone
        : presentation
            ? primaryTimeZone(presentation)
            : null;
    return useZonedFormat(zone, options);
};
